import PropTypes from "prop-types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { addFeedback, removeFeedback, addSurveyComment } from "actions/happiness-survey-actions";
import { pages, qualityIssueOptionsWithCopy } from "constants/happiness-survey-values";
import SurveyItemBanner from "./item-banner";
import SurveyItemHeader from "./feedback-header";
import InputContainer from "./inputs/input-container";
import ButtonContainer from "./inputs/button-container";
import FeedbackFooter from "./feedback-footer";

const propTypes = {
  currentSurveyIndex: PropTypes.number.isRequired,
  closeFeedbackScreen: PropTypes.func,
};

const QualityIssues = ({ currentSurveyIndex, closeFeedbackScreen = () => {} }) => {
  const currentSurvey = useSelector(state => state.happinessSurvey.itemSurveys[currentSurveyIndex]);
  if (!currentSurvey) return null;

  const [comment, setComment] = React.useState(currentSurvey.comment || "");
  const dispatch = useDispatch();

  const commentLabelProps = {
    headerText: "Anything else we should know?",
  };
  const optionsLabelProps = {
    headerText: "What was wrong with this item?",
    subHeaderText: "Select all that apply",
    isRequired: true,
  };

  const selectedButtons = React.useMemo(() => {
    const selected = new Set();
    currentSurvey.surveyFeedbacks?.forEach(fbEntry => {
      if (fbEntry.feedback in qualityIssueOptionsWithCopy) {
        selected.add(fbEntry.feedback);
      }
    });
    return selected;
  }, [currentSurvey.surveyFeedbacks]);

  const handleSave = () => {
    if (comment) {
      dispatch(addSurveyComment(comment, currentSurvey.bookingId));
    }
    closeFeedbackScreen();
  };

  const handleClickSelectionButton = data => {
    if (!selectedButtons.has(data)) {
      dispatch(addFeedback(data, currentSurvey.bookingId));
    } else {
      dispatch(removeFeedback(data, currentSurvey.bookingId));
    }
  };

  return (
    <div className="flex flex-col h-full" data-test-id="happiness-survey-quality-issues-screen">
      <SurveyItemHeader
        title="Report an issue"
        subtitle="Let us know what was wrong with this item so we can fix it for you and other members."
        closeFeedbackScreen={closeFeedbackScreen}
      />

      <div className="flex flex-col px-4 grow">
        <SurveyItemBanner currentSurvey={currentSurvey} />
        <div className="mt-6 grow">
          <InputContainer labelProps={optionsLabelProps}>
            <ButtonContainer
              selectedButtons={selectedButtons}
              buttons={qualityIssueOptionsWithCopy}
              hasFullWidthButtons={false}
              onClickSelectionButton={handleClickSelectionButton}
            />
          </InputContainer>

          <InputContainer labelProps={commentLabelProps}>
            <textarea
              maxLength="2000"
              value={comment}
              placeholder="Leave a comment for the RTR team"
              onChange={e => setComment(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </InputContainer>
        </div>
      </div>

      <FeedbackFooter ctaCopy="Save Issue" onClick={handleSave} />
    </div>
  );
};

QualityIssues.pageName = pages.QUALITY_ISSUES;
QualityIssues.propTypes = propTypes;

export default QualityIssues;
